import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// MUI Core Components
import {
  // Layout
  Box,
  Paper,
  Grid,
  
  // Typography
  Typography,
  
  // Inputs
  TextField,
  Button,
  IconButton,
  Fab,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormLabel,
  FormHelperText,
  InputLabel,
  InputAdornment,
  
  // Data Display
  Chip,
  Divider,
  
  // Feedback
  Alert,
  Snackbar,
  CircularProgress,
  LinearProgress,
  
  // Surfaces
  Card,
  CardContent,
  
  // Navigation
  Menu,
  
  // Tables
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  
  // Utils
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from '@mui/material';

// Icons
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import InventoryIcon from '@mui/icons-material/Inventory';
import StorageIcon from '@mui/icons-material/Storage';
import MemoryIcon from '@mui/icons-material/Memory';
import BuildIcon from '@mui/icons-material/Build';
import CableIcon from '@mui/icons-material/Cable';
import ComputerIcon from '@mui/icons-material/Computer';
import WarningIcon from '@mui/icons-material/Warning';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import VerifiedIcon from '@mui/icons-material/Verified';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import RemoveIcon from '@mui/icons-material/Remove';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import PostAddIcon from '@mui/icons-material/PostAdd';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Refresh from '@mui/icons-material/Refresh';

// API imports
import apiService, { 
  getComputerParts, 
  addComputerPart, 
  updateComputerPart,
  deleteComputerPart,
  searchComputerParts,
  updatePartStock,
  getPartStats,
  getLowStockParts
} from '../../services/api';

const InventoryManagement = () => {
  const navigate = useNavigate();
  
  // Safe number parsing helpers
  const safeParseInt = (value, defaultValue = 0) => {
    if (value === null || value === undefined || value === '') return defaultValue;
    const parsed = parseInt(value);
    return isNaN(parsed) ? defaultValue : parsed;
  };

  const safeParseFloat = (value, defaultValue = 0) => {
    if (value === null || value === undefined || value === '') return defaultValue;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? defaultValue : parsed;
  };

  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [openAdjustmentDialog, setOpenAdjustmentDialog] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [stats, setStats] = useState({
    totalParts: 0,
    totalValue: 0,
    lowStockCount: 0,
    outOfStockCount: 0
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [formMode, setFormMode] = useState('add');
  const [adjustmentData, setAdjustmentData] = useState({
    quantity: '',
    adjustment: '',
    reason: '',
    adjustment_category: 'normal'
  });
  const [adjustmentType, setAdjustmentType] = useState('adjustment');
  const [categories, setCategories] = useState([]);

  // State for the enhanced add part modal
  const [openAddPartDialog, setOpenAddPartDialog] = useState(false);
  const [addPartMode, setAddPartMode] = useState('new');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedExistingPart, setSelectedExistingPart] = useState(null);
  const [additionalQuantity, setAdditionalQuantity] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [saving, setSaving] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [newPartData, setNewPartData] = useState({
    part_name: '',
    part_number: '',
    category: '',
    manufacturer: '',
    supplier: '',
    purchase_price: '',
    selling_price: '',
    quantity: '0',
    min_stock_level: '10',
    location: 'Main Store',
    specifications: '',
    notes: ''
  });

  const partCategories = [
    'Processor', 'Motherboard', 'RAM', 'Storage', 'Graphics Card',
    'Power Supply', 'Computer Cabinet', 'Monitor', 'Keyboard', 'Mouse',
    'Cables & Accessories', 'Cooling System', 'Network Components', 'UPS', 'Other'
  ];

  const locations = [
    'Main Store', 'Warehouse A', 'Warehouse B', 'Technician Room',
    'Project Storage', 'Repair Center', 'School Site Storage'
  ];

  const manufacturers = [
    'Intel', 'AMD', 'NVIDIA', 'ASUS', 'MSI', 'Gigabyte', 'ASRock',
    'Samsung', 'Western Digital', 'Seagate', 'Kingston', 'Corsair',
    'Cooler Master', 'Logitech', 'Dell', 'HP', 'Lenovo', 'Acer',
    'Apple', 'Microsoft', 'Other'
  ];

  useEffect(() => {
    fetchParts();
    fetchStats();
    fetchCategories();
  }, []);

  // Log formData changes for debugging
  useEffect(() => {
    if (dataLoaded) {
      console.log('📊 Current formData loaded');
    }
  }, [dataLoaded]);

  const fetchParts = async () => {
    try {
      setLoading(true);
      const response = await getComputerParts();
      if (response.success) {
        const processedParts = (response.data || []).map(part => {
          const quantity = safeParseInt(part.quantity);
          const minStock = safeParseInt(part.min_stock_level, 10);
          return {
            ...part,
            quantity: quantity,
            min_stock_level: minStock,
            purchase_price: safeParseFloat(part.purchase_price),
            selling_price: safeParseFloat(part.selling_price),
            is_low_stock: quantity > 0 && quantity <= minStock
          };
        });
        setParts(processedParts);
        calculateStats(processedParts);
      }
    } catch (err) {
      showError(err.message || 'Failed to load parts');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await getPartStats();
      if (response.success) {
        setStats(prev => ({
          ...prev,
          totalParts: response.data.total_parts || 0,
          totalValue: response.data.inventory_value || 0,
          lowStockCount: response.data.low_stock_count || 0
        }));
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiService.getPartCategories();
      if (response.success) {
        setCategories(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const calculateStats = (partsData) => {
    let totalValue = 0;
    let lowStockCount = 0;
    let outOfStockCount = 0;
    
    partsData.forEach(part => {
      const purchasePrice = safeParseFloat(part.purchase_price);
      const quantity = safeParseInt(part.quantity);
      const minStock = safeParseInt(part.min_stock_level, 10);
      
      totalValue += purchasePrice * quantity;
      
      if (quantity === 0) {
        outOfStockCount++;
      } else if (quantity <= minStock) {
        lowStockCount++;
      }
    });
    
    setStats({
      totalParts: partsData.length,
      totalValue: totalValue,
      lowStockCount,
      outOfStockCount
    });
  };

  const showSuccess = (message) => {
    setSnackbarMessage(message);
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  const showError = (message) => {
    setSnackbarMessage(message);
    setSnackbarSeverity('error');
    setSnackbarOpen(true);
  };

  const handleDeleteClick = (part) => {
    setSelectedPart(part);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await deleteComputerPart(selectedPart.id);
      if (response.success) {
        showSuccess('Part deleted successfully!');
        setOpenDeleteDialog(false);
        fetchParts();
        fetchStats();
      } else {
        showError(response.message || 'Failed to delete part');
      }
    } catch (err) {
      showError(err.message || 'Failed to delete part');
    }
  };

  const handleView = (part) => {
    setSelectedPart(part);
    setOpenViewDialog(true);
  };

  const handleEdit = (part) => {
    setSelectedPart(part);
    setFormMode('edit');
    setOpenFormDialog(true);
  };

  const handleAddPart = () => {
    setAddPartMode('new');
    setSelectedExistingPart(null);
    setAdditionalQuantity('');
    setSearchResults([]);
    setShowSearchResults(false);
    setSearchQuery('');
    generateNewPartNumber();
    setOpenAddPartDialog(true);
  };

  const generateNewPartNumber = () => {
    const prefix = 'PART';
    const year = new Date().getFullYear().toString().slice(-2);
    const month = (new Date().getMonth() + 1).toString().padStart(2, '0');
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    setNewPartData(prev => ({ ...prev, part_number: `${prefix}${year}${month}${random}` }));
  };

  const handleNewPartChange = (e) => {
    const { name, value } = e.target;
    setNewPartData(prev => ({ ...prev, [name]: value }));
  };

  // DEBUG VERSION: Search for existing parts with detailed logging
  const handleSearchExisting = async () => {
    if (!searchQuery.trim()) {
      showError('Please enter a search term');
      return;
    }

    try {
      setSearching(true);
      
      const query = searchQuery.trim();
      console.log('🔍 =========================================');
      console.log('🔍 Searching computer parts for:', query);
      console.log('🔍 =========================================');
      
      const response = await searchComputerParts(query);
      console.log('📦 RAW API RESPONSE:', response);
      console.log('🔍 =========================================');
      
      if (response && response.success) {
        console.log('📊 Response data:', response.data);
        console.log('📊 Number of items:', response.data?.length);
        console.log('🔍 =========================================');
        
        const results = (response.data || []).map((part, index) => {
          console.log(`\n--- ITEM ${index + 1}: ${part.part_name || 'Unnamed'} ---`);
          console.log('Full part object:', JSON.stringify(part, null, 2));
          console.log('--- Checking quantity locations ---');
          
          // Check all possible quantity locations
          console.log('1. part.quantity:', part.quantity);
          console.log('   Type:', typeof part.quantity);
          
          if (part.SchoolProjectInventory) {
            console.log('2. part.SchoolProjectInventory.quantity:', part.SchoolProjectInventory.quantity);
          } else {
            console.log('2. part.SchoolProjectInventory: NOT FOUND');
          }
          
          if (part.SchoolProjectInventories) {
            console.log('3. part.SchoolProjectInventories:', part.SchoolProjectInventories);
            if (part.SchoolProjectInventories.length > 0) {
              console.log('   First item quantity:', part.SchoolProjectInventories[0].quantity);
            }
          } else {
            console.log('3. part.SchoolProjectInventories: NOT FOUND');
          }
          
          if (part.inventory) {
            console.log('4. part.inventory.quantity:', part.inventory.quantity);
          } else {
            console.log('4. part.inventory: NOT FOUND');
          }
          
          if (part.data) {
            console.log('5. part.data.quantity:', part.data.quantity);
          } else {
            console.log('5. part.data: NOT FOUND');
          }
          
          // Try to extract quantity from all possible locations
          let quantity = 0;
          let quantitySource = 'none';
          
          // Check direct property
          if (part.quantity !== undefined && part.quantity !== null) {
            quantity = safeParseInt(part.quantity);
            quantitySource = 'part.quantity';
            console.log(`✅ Using ${quantitySource} =`, quantity);
          }
          // Check SchoolProjectInventory
          else if (part.SchoolProjectInventory && part.SchoolProjectInventory.quantity !== undefined) {
            quantity = safeParseInt(part.SchoolProjectInventory.quantity);
            quantitySource = 'part.SchoolProjectInventory.quantity';
            console.log(`✅ Using ${quantitySource} =`, quantity);
          }
          // Check SchoolProjectInventories array
          else if (part.SchoolProjectInventories && part.SchoolProjectInventories.length > 0) {
            const inv = part.SchoolProjectInventories[0];
            if (inv.quantity !== undefined) {
              quantity = safeParseInt(inv.quantity);
              quantitySource = 'part.SchoolProjectInventories[0].quantity';
              console.log(`✅ Using ${quantitySource} =`, quantity);
            }
          }
          // Check inventory
          else if (part.inventory && part.inventory.quantity !== undefined) {
            quantity = safeParseInt(part.inventory.quantity);
            quantitySource = 'part.inventory.quantity';
            console.log(`✅ Using ${quantitySource} =`, quantity);
          }
          // Check data
          else if (part.data && part.data.quantity !== undefined) {
            quantity = safeParseInt(part.data.quantity);
            quantitySource = 'part.data.quantity';
            console.log(`✅ Using ${quantitySource} =`, quantity);
          }
          
          if (quantitySource === 'none') {
            console.log('❌ NO QUANTITY FOUND ANYWHERE!');
          }
          
          // Get location
          let location = 'Main Store';
          if (part.location) {
            location = part.location;
          } else if (part.SchoolProjectInventory?.location) {
            location = part.SchoolProjectInventory.location;
          } else if (part.SchoolProjectInventories?.[0]?.location) {
            location = part.SchoolProjectInventories[0].location;
          }
          
          // Get min stock level
          let minStock = 10;
          if (part.min_stock_level !== undefined) {
            minStock = safeParseInt(part.min_stock_level, 10);
          } else if (part.SchoolProjectInventory?.min_stock_level !== undefined) {
            minStock = safeParseInt(part.SchoolProjectInventory.min_stock_level, 10);
          } else if (part.SchoolProjectInventories?.[0]?.min_stock_level !== undefined) {
            minStock = safeParseInt(part.SchoolProjectInventories[0].min_stock_level, 10);
          }
          
          console.log(`📍 Location: ${location}`);
          console.log(`📊 Min Stock: ${minStock}`);
          console.log(`🔢 Final quantity: ${quantity} (from ${quantitySource})`);
          console.log('--- End of item ---\n');
          
          return {
            id: part.id,
            part_name: part.part_name || '',
            part_number: part.part_number || '',
            category: part.category || '',
            manufacturer: part.manufacturer || '',
            purchase_price: safeParseFloat(part.purchase_price),
            quantity: quantity,
            min_stock_level: minStock,
            location: location,
            is_low_stock: quantity > 0 && quantity <= minStock,
            quantity_source: quantitySource // For debugging
          };
        });
        
        console.log('🔍 =========================================');
        console.log('📊 FINAL PROCESSED RESULTS:', results);
        console.log('🔍 =========================================');
        
        setSearchResults(results);
        setShowSearchResults(true);
        setPage(0);
        
        if (results.length === 0) {
          showError('No computer parts found. Try a different search term.');
        } else {
          showSuccess(`Found ${results.length} parts matching "${query}"`);
        }
      }
    } catch (err) {
      console.error('Search error:', err);
      showError(err.message || 'Failed to search computer parts');
    } finally {
      setSearching(false);
    }
  };

  // Select a part from search results
  const handleSelectExistingPart = (part) => {
    console.log('✅ SELECTED PART:', part);
    console.log('   Quantity:', part.quantity);
    console.log('   Quantity source:', part.quantity_source);
    setSelectedExistingPart(part);
    setShowSearchResults(false);
    setSearchQuery('');
    setAdditionalQuantity('');
    setNewPartData(prev => ({
      ...prev,
      location: part.location || 'Main Store'
    }));
    showSuccess(`Selected: ${part.part_name} (Current Stock: ${part.quantity})`);
  };

  // Clear selected part
  const handleClearSelected = () => {
    setSelectedExistingPart(null);
    setAdditionalQuantity('');
    setSearchResults([]);
  };

  // Handle add part submit - WORKS WITH EXISTING PART
  const handleAddPartSubmit = async () => {
    try {
      setSaving(true);

      if (addPartMode === 'existing' && selectedExistingPart) {
        // Add to existing part
        if (!additionalQuantity || safeParseInt(additionalQuantity) <= 0) {
          showError('Please enter a valid quantity to add');
          setSaving(false);
          return;
        }

        const currentQuantity = safeParseInt(selectedExistingPart.quantity);
        const addQuantity = safeParseInt(additionalQuantity);
        const newQuantity = currentQuantity + addQuantity;
        
        // Create update data object with only the fields we want to update
        const updateData = {
          quantity: newQuantity,
          location: newPartData.location || selectedExistingPart.location || 'Main Store'
        };

        console.log('Updating existing part with data:', updateData);
        console.log('Part ID:', selectedExistingPart.id);
        
        // Use updateComputerPart to update the existing part
        const response = await updateComputerPart(selectedExistingPart.id, updateData);
        console.log('Update response:', response);
        
        if (response && response.success) {
          showSuccess(
            `✅ Successfully added ${addQuantity} units to ${selectedExistingPart.part_name}. ` +
            `New total: ${newQuantity} units`
          );
          setOpenAddPartDialog(false);
          fetchParts();
          fetchStats();
        } else {
          showError(response?.message || 'Failed to update part');
        }
      } else {
        // Create new part
        if (!newPartData.part_name || !newPartData.category || !newPartData.purchase_price) {
          showError('Part name, category, and purchase price are required');
          setSaving(false);
          return;
        }

        const dataToSend = {
          ...newPartData,
          purchase_price: safeParseFloat(newPartData.purchase_price),
          selling_price: safeParseFloat(newPartData.selling_price),
          quantity: safeParseInt(newPartData.quantity),
          min_stock_level: safeParseInt(newPartData.min_stock_level, 10)
        };

        console.log('Creating new part with data:', dataToSend);
        
        const response = await addComputerPart(dataToSend);

        if (response && response.success) {
          showSuccess('✅ Computer part created successfully!');
          setOpenAddPartDialog(false);
          fetchParts();
          fetchStats();
        } else {
          showError(response?.message || 'Failed to create part');
        }
      }
    } catch (err) {
      console.error('Save error:', err);
      showError(err.message || `Failed to ${addPartMode === 'existing' ? 'add to' : 'create'} computer part`);
    } finally {
      setSaving(false);
    }
  };

  const handleAdjustment = (part) => {
    setSelectedPart(part);
    setAdjustmentData({
      quantity: '',
      adjustment: '',
      reason: '',
      adjustment_category: 'normal'
    });
    setAdjustmentType('adjustment');
    setOpenAdjustmentDialog(true);
  };

  const handleAdjustmentSubmit = async () => {
    try {
      const dataToSend = {
        reason: adjustmentData.reason,
        adjustment_category: adjustmentData.adjustment_category
      };

      if (adjustmentType === 'set') {
        dataToSend.quantity = safeParseInt(adjustmentData.quantity);
      } else {
        dataToSend.adjustment = safeParseInt(adjustmentData.adjustment);
      }

      const response = await updatePartStock(selectedPart.id, dataToSend);

      if (response.success) {
        showSuccess('Stock adjustment completed successfully!');
        setOpenAdjustmentDialog(false);
        fetchParts();
        fetchStats();
      } else {
        showError(response.message || 'Failed to update stock');
      }
    } catch (err) {
      showError(err.message || 'Failed to update stock');
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleCategoryFilterChange = (category) => {
    setCategoryFilter(category);
    handleFilterClose();
  };

  const handleStockFilterChange = (filter) => {
    setStockFilter(filter);
    handleFilterClose();
  };

  const handleClearFilters = () => {
    setCategoryFilter('all');
    setStockFilter('all');
    setSearchQuery('');
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatCurrency = (amount) => {
    const safeAmount = safeParseFloat(amount);
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2
    }).format(safeAmount);
  };

  const calculateInventoryValue = (part) => {
    if (!part) return 0;
    const price = safeParseFloat(part.purchase_price);
    const quantity = safeParseInt(part.quantity);
    return price * quantity;
  };

  const getStockStatusColor = (quantity, minStock) => {
    const safeQty = safeParseInt(quantity);
    const safeMin = safeParseInt(minStock, 10);
    
    if (safeQty <= 0) return 'error';
    if (safeQty <= safeMin) return 'warning';
    return 'success';
  };

  const getCategoryIcon = (category) => {
    if (!category) return <BuildIcon fontSize="small" />;
    const categoryLower = category.toLowerCase();
    
    if (categoryLower.includes('processor') || categoryLower.includes('cpu')) {
      return <MemoryIcon fontSize="small" />;
    }
    if (categoryLower.includes('motherboard')) {
      return <ComputerIcon fontSize="small" />;
    }
    if (categoryLower.includes('ram')) {
      return <MemoryIcon fontSize="small" />;
    }
    if (categoryLower.includes('storage') || categoryLower.includes('hdd') || categoryLower.includes('ssd')) {
      return <StorageIcon fontSize="small" />;
    }
    if (categoryLower.includes('graphics') || categoryLower.includes('gpu')) {
      return <ComputerIcon fontSize="small" />;
    }
    if (categoryLower.includes('power') || categoryLower.includes('psu')) {
      return <CableIcon fontSize="small" />;
    }
    if (categoryLower.includes('cabinet') || categoryLower.includes('case')) {
      return <ComputerIcon fontSize="small" />;
    }
    return <BuildIcon fontSize="small" />;
  };

  const getCategoryColor = (category) => {
    if (!category) return '#9e9e9e';
    const categoryLower = category.toLowerCase();
    
    if (categoryLower.includes('processor') || categoryLower.includes('cpu')) return '#2196f3';
    if (categoryLower.includes('motherboard')) return '#4caf50';
    if (categoryLower.includes('ram')) return '#9c27b0';
    if (categoryLower.includes('storage')) return '#ff9800';
    if (categoryLower.includes('graphics')) return '#f44336';
    if (categoryLower.includes('power')) return '#607d8b';
    if (categoryLower.includes('cabinet')) return '#795548';
    return '#9e9e9e';
  };

  const filteredParts = parts.filter(part => {
    if (searchQuery && 
        !part.part_name?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !part.part_number?.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    if (stockFilter === 'low' && !part.is_low_stock) return false;
    if (stockFilter === 'out' && safeParseInt(part.quantity) > 0) return false;
    if (categoryFilter !== 'all' && part.category !== categoryFilter) return false;
    
    return true;
  });

  const exportToCSV = () => {
    try {
      const dataToExport = filteredParts.length > 0 ? filteredParts : parts;
      
      if (dataToExport.length === 0) {
        showError('No data to export');
        return;
      }

      const headers = [
        'Part Number', 'Part Name', 'Category', 'Manufacturer', 'Supplier',
        'Purchase Price (LKR)', 'Selling Price (LKR)', 'Quantity',
        'Min Stock Level', 'Location', 'Inventory Value (LKR)', 'Specifications', 'Notes'
      ];

      const csvRows = dataToExport.map(part => {
        const inventoryValue = calculateInventoryValue(part);
        return [
          `"${part.part_number || ''}"`,
          `"${part.part_name || ''}"`,
          `"${part.category || ''}"`,
          `"${part.manufacturer || ''}"`,
          `"${part.supplier || ''}"`,
          safeParseFloat(part.purchase_price) || 0,
          safeParseFloat(part.selling_price) || 0,
          safeParseInt(part.quantity) || 0,
          safeParseInt(part.min_stock_level, 10) || 10,
          `"${part.location || 'Main Store'}"`,
          safeParseFloat(inventoryValue) || 0,
          `"${(part.specifications || '').replace(/"/g, '""')}"`,
          `"${(part.notes || '').replace(/"/g, '""')}"`
        ].join(',');
      });

      const csvContent = [headers.join(','), ...csvRows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `inventory_export_${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showSuccess(`Exported ${dataToExport.length} parts to CSV`);
    } catch (err) {
      showError('Failed to export CSV: ' + err.message);
    }
  };

  const exportAllToCSV = () => {
    try {
      if (parts.length === 0) {
        showError('No data to export');
        return;
      }

      const headers = [
        'Part Number', 'Part Name', 'Category', 'Manufacturer', 'Supplier',
        'Purchase Price (LKR)', 'Selling Price (LKR)', 'Quantity',
        'Min Stock Level', 'Location', 'Inventory Value (LKR)', 'Specifications', 'Notes'
      ];

      const csvRows = parts.map(part => {
        const inventoryValue = calculateInventoryValue(part);
        return [
          `"${part.part_number || ''}"`,
          `"${part.part_name || ''}"`,
          `"${part.category || ''}"`,
          `"${part.manufacturer || ''}"`,
          `"${part.supplier || ''}"`,
          safeParseFloat(part.purchase_price) || 0,
          safeParseFloat(part.selling_price) || 0,
          safeParseInt(part.quantity) || 0,
          safeParseInt(part.min_stock_level, 10) || 10,
          `"${part.location || 'Main Store'}"`,
          safeParseFloat(inventoryValue) || 0,
          `"${(part.specifications || '').replace(/"/g, '""')}"`,
          `"${(part.notes || '').replace(/"/g, '""')}"`
        ].join(',');
      });

      const csvContent = [headers.join(','), ...csvRows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `inventory_full_export_${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showSuccess(`Exported all ${parts.length} parts to CSV`);
    } catch (err) {
      showError('Failed to export CSV: ' + err.message);
    }
  };

  const PartForm = () => {
    const [formData, setFormData] = useState({
      part_name: '',
      part_number: '',
      category: '',
      manufacturer: '',
      supplier: '',
      purchase_price: '',
      selling_price: '',
      quantity: '0',
      min_stock_level: '10',
      location: 'Main Store',
      specifications: '',
      notes: ''
    });
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState(null);

    useEffect(() => {
      if (formMode === 'edit' && selectedPart) {
        setFormData({
          part_name: selectedPart.part_name || '',
          part_number: selectedPart.part_number || '',
          category: selectedPart.category || '',
          manufacturer: selectedPart.manufacturer || '',
          supplier: selectedPart.supplier || '',
          purchase_price: selectedPart.purchase_price ? selectedPart.purchase_price.toString() : '',
          selling_price: selectedPart.selling_price ? selectedPart.selling_price.toString() : '',
          quantity: safeParseInt(selectedPart.quantity).toString() || '0',
          min_stock_level: safeParseInt(selectedPart.min_stock_level, 10).toString() || '10',
          location: selectedPart.location || 'Main Store',
          specifications: selectedPart.specifications || '',
          notes: selectedPart.notes || ''
        });
        setDataLoaded(true);
      } else {
        generatePartNumber();
        setDataLoaded(true);
      }
    }, [formMode, selectedPart]);

    const generatePartNumber = () => {
      const prefix = 'PART';
      const year = new Date().getFullYear().toString().slice(-2);
      const month = (new Date().getMonth() + 1).toString().padStart(2, '0');
      const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
      setFormData(prev => ({ ...prev, part_number: `${prefix}${year}${month}${random}` }));
    };

    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setFormError(null);

      if (!formData.part_name || !formData.category || !formData.purchase_price) {
        setFormError('Please fill all required fields');
        return;
      }

      try {
        setSaving(true);
        const dataToSend = {
          ...formData,
          purchase_price: safeParseFloat(formData.purchase_price),
          selling_price: safeParseFloat(formData.selling_price),
          quantity: safeParseInt(formData.quantity),
          min_stock_level: safeParseInt(formData.min_stock_level, 10)
        };

        if (formMode === 'edit' && selectedPart) {
          const response = await updateComputerPart(selectedPart.id, dataToSend);
          if (response.success) {
            showSuccess('Part updated successfully!');
            setOpenFormDialog(false);
            fetchParts();
            fetchStats();
          } else {
            setFormError(response.message || 'Failed to update part');
          }
        } else {
          const response = await addComputerPart(dataToSend);
          if (response.success) {
            showSuccess('Part added successfully!');
            setOpenFormDialog(false);
            fetchParts();
            fetchStats();
          } else {
            setFormError(response.message || 'Failed to add part');
          }
        }
      } catch (err) {
        setFormError(err.message || 'Failed to save part');
      } finally {
        setSaving(false);
      }
    };

    return (
      <Box sx={{ p: 3 }} key={formKey}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          {formMode === 'edit' ? 'Edit Computer Part' : 'Add New Computer Part'}
        </Typography>
        
        {formError && (
          <Alert severity="error" sx={{ mb: 3 }}>{formError}</Alert>
        )}
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Part Name *"
                name="part_name"
                value={formData.part_name}
                onChange={handleChange}
                required
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Part Number"
                name="part_number"
                value={formData.part_number}
                onChange={handleChange}
                margin="normal"
                InputProps={{
                  readOnly: formMode === 'edit',
                  endAdornment: formMode !== 'edit' && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={generatePartNumber}>
                        <Tooltip title="Generate New Number">
                          <Refresh fontSize="small" />
                        </Tooltip>
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Category *</InputLabel>
                <Select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  label="Category *"
                >
                  {partCategories.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Manufacturer</InputLabel>
                <Select
                  name="manufacturer"
                  value={formData.manufacturer}
                  onChange={handleChange}
                  label="Manufacturer"
                >
                  {manufacturers.map(mfg => (
                    <MenuItem key={mfg} value={mfg}>{mfg}</MenuItem>
                  ))}
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Purchase Price (LKR) *"
                name="purchase_price"
                value={formData.purchase_price}
                onChange={handleChange}
                type="number"
                InputProps={{ 
                  inputProps: { min: 0, step: 0.01 },
                  startAdornment: <InputAdornment position="start">Rs.</InputAdornment>
                }}
                required
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Selling Price (LKR)"
                name="selling_price"
                value={formData.selling_price}
                onChange={handleChange}
                type="number"
                InputProps={{ 
                  inputProps: { min: 0, step: 0.01 },
                  startAdornment: <InputAdornment position="start">Rs.</InputAdornment>
                }}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Initial Quantity"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                type="number"
                InputProps={{ inputProps: { min: 0 } }}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Min Stock Level"
                name="min_stock_level"
                value={formData.min_stock_level}
                onChange={handleChange}
                type="number"
                InputProps={{ inputProps: { min: 0 } }}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Location</InputLabel>
                <Select
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  label="Location"
                >
                  {locations.map(loc => (
                    <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Supplier"
                name="supplier"
                value={formData.supplier}
                onChange={handleChange}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Specifications"
                name="specifications"
                value={formData.specifications}
                onChange={handleChange}
                multiline
                rows={2}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                multiline
                rows={2}
                margin="normal"
              />
            </Grid>
          </Grid>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => setOpenFormDialog(false)}
              disabled={saving}
              startIcon={<CloseIcon />}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={saving}
              sx={{ backgroundColor: '#3498db' }}
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {saving ? 'Saving...' : (formMode === 'edit' ? 'Update Part' : 'Save Part')}
            </Button>
          </Box>
        </form>
      </Box>
    );
  };

  const AdjustmentDialog = () => {
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState(null);

    const calculateNewQuantity = () => {
      const currentQuantity = safeParseInt(selectedPart?.quantity);
      
      if (adjustmentType === 'set') {
        return safeParseInt(adjustmentData.quantity) || currentQuantity;
      } else {
        const adjustment = safeParseInt(adjustmentData.adjustment);
        return currentQuantity + adjustment;
      }
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      
      try {
        setSaving(true);
        await handleAdjustmentSubmit();
      } catch (err) {
        setFormError(err.message || 'Failed to update stock');
      } finally {
        setSaving(false);
      }
    };

    const newQuantity = calculateNewQuantity();
    const currentQuantity = safeParseInt(selectedPart?.quantity);

    return (
      <Dialog open={openAdjustmentDialog} onClose={() => setOpenAdjustmentDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6">
            Stock Adjustment: {selectedPart?.part_name || 'Part'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setFormError(null)}>
              {formError}
            </Alert>
          )}
          
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Current Stock Information
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Current Stock:</Typography>
                    <Typography variant="body1">{currentQuantity} pcs</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Min Stock Level:</Typography>
                    <Typography variant="body1">{selectedPart?.min_stock_level || 10} pcs</Typography>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Adjustment Preview
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">New Stock:</Typography>
                    <Typography variant="body1" color={newQuantity >= currentQuantity ? 'success.main' : 'error.main'}>
                      {newQuantity} pcs
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Change:</Typography>
                    <Typography variant="body1" color={newQuantity >= currentQuantity ? 'success.main' : 'error.main'}>
                      {newQuantity >= currentQuantity ? '+' : ''}{newQuantity - currentQuantity} pcs
                    </Typography>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 2 }} />
          
          <form onSubmit={handleSubmit}>
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">Adjustment Type</FormLabel>
              <RadioGroup
                row
                value={adjustmentType}
                onChange={(e) => setAdjustmentType(e.target.value)}
              >
                <FormControlLabel 
                  value="adjustment" 
                  control={<Radio />} 
                  label="Adjustment (Add/Remove)" 
                />
                <FormControlLabel 
                  value="set" 
                  control={<Radio />} 
                  label="Set Specific Quantity" 
                />
              </RadioGroup>
            </FormControl>
            
            {adjustmentType === 'set' ? (
              <TextField
                fullWidth
                label="Set Quantity To *"
                value={adjustmentData.quantity}
                onChange={(e) => setAdjustmentData({...adjustmentData, quantity: e.target.value})}
                type="number"
                InputProps={{ inputProps: { min: 0 } }}
                required
                margin="normal"
                helperText={`Current: ${currentQuantity} pcs`}
              />
            ) : (
              <TextField
                fullWidth
                label="Adjustment Amount *"
                value={adjustmentData.adjustment}
                onChange={(e) => setAdjustmentData({...adjustmentData, adjustment: e.target.value})}
                type="number"
                helperText={`Enter positive number to add, negative to remove. Current: ${currentQuantity} pcs`}
                required
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {safeParseInt(adjustmentData.adjustment) > 0 ? <AddIcon color="success" /> : <RemoveIcon color="error" />}
                    </InputAdornment>
                  )
                }}
              />
            )}
            
            <TextField
              fullWidth
              label="Reason for Adjustment *"
              value={adjustmentData.reason}
              onChange={(e) => setAdjustmentData({...adjustmentData, reason: e.target.value})}
              required
              margin="normal"
              multiline
              rows={2}
            />
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="outlined"
                startIcon={<CloseIcon />}
                onClick={() => setOpenAdjustmentDialog(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                disabled={saving}
                sx={{ backgroundColor: '#3498db' }}
              >
                {saving ? 'Updating...' : 'Update Stock'}
              </Button>
            </Box>
          </form>
        </DialogContent>
      </Dialog>
    );
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }} key={formKey}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ color: '#2c3e50', fontWeight: 'bold' }}>
            School Projects Inventory
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {filteredParts.length} parts found • {stats.lowStockCount} low stock • {stats.outOfStockCount} out of stock
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportToCSV}
            size="small"
            disabled={filteredParts.length === 0}
            title="Export filtered results"
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportAllToCSV}
            size="small"
            disabled={parts.length === 0}
            title="Export all data"
            sx={{ ml: 1 }}
          >
            Export All
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchParts}
            size="small"
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: '#3498db',
              '&:hover': { backgroundColor: '#2980b9' }
            }}
            onClick={handleAddPart}
          >
            Add Part
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Total Parts
                  </Typography>
                  <Typography variant="h4">{stats.totalParts}</Typography>
                </Box>
                <InventoryIcon sx={{ fontSize: 40, color: '#3498db', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Total Value
                  </Typography>
                  <Typography variant="h4">
                    {formatCurrency(stats.totalValue)}
                  </Typography>
                </Box>
                <AttachMoneyIcon sx={{ fontSize: 40, color: '#2ecc71', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Low Stock
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {stats.lowStockCount}
                  </Typography>
                </Box>
                <WarningIcon sx={{ fontSize: 40, color: '#f39c12', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Out of Stock
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {stats.outOfStockCount}
                  </Typography>
                </Box>
                <LocalShippingIcon sx={{ fontSize: 40, color: '#e74c3c', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <TextField
          placeholder="Search parts by name, number, or category..."
          variant="outlined"
          size="small"
          fullWidth
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={handleFilterClick}
          sx={{ minWidth: 120 }}
        >
          Filter
        </Button>
        <Menu
          anchorEl={filterAnchorEl}
          open={Boolean(filterAnchorEl)}
          onClose={handleFilterClose}
        >
          <MenuItem disabled>Category</MenuItem>
          <MenuItem 
            selected={categoryFilter === 'all'}
            onClick={() => handleCategoryFilterChange('all')}
          >
            All Categories
          </MenuItem>
          {partCategories.map(category => (
            <MenuItem 
              key={category}
              selected={categoryFilter === category}
              onClick={() => handleCategoryFilterChange(category)}
            >
              {category}
            </MenuItem>
          ))}
          <MenuItem disabled>Stock Status</MenuItem>
          <MenuItem 
            selected={stockFilter === 'all'}
            onClick={() => handleStockFilterChange('all')}
          >
            All Stock
          </MenuItem>
          <MenuItem 
            selected={stockFilter === 'low'}
            onClick={() => handleStockFilterChange('low')}
          >
            Low Stock Only
          </MenuItem>
          <MenuItem 
            selected={stockFilter === 'out'}
            onClick={() => handleStockFilterChange('out')}
          >
            Out of Stock
          </MenuItem>
          <MenuItem disabled>Export</MenuItem>
          <MenuItem 
            onClick={() => {
              handleFilterClose();
              exportToCSV();
            }}
            disabled={filteredParts.length === 0}
          >
            <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
            Export Filtered Results ({filteredParts.length})
          </MenuItem>
          <MenuItem 
            onClick={() => {
              handleFilterClose();
              exportAllToCSV();
            }}
            disabled={parts.length === 0}
          >
            <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
            Export All Data ({parts.length})
          </MenuItem>
          {(categoryFilter !== 'all' || stockFilter !== 'all') && (
            <MenuItem 
              onClick={handleClearFilters}
              sx={{ color: 'primary.main', fontWeight: 'medium' }}
            >
              Clear All Filters
            </MenuItem>
          )}
        </Menu>
      </Paper>

      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>Part Number</strong></TableCell>
              <TableCell><strong>Part Name</strong></TableCell>
              <TableCell><strong>Category</strong></TableCell>
              <TableCell><strong>Price</strong></TableCell>
              <TableCell><strong>Stock</strong></TableCell>
              <TableCell><strong>Value</strong></TableCell>
              <TableCell><strong>Location</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredParts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No parts found. Try adjusting your search or filters.
                  </Typography>
                  <Button 
                    variant="outlined" 
                    startIcon={<AddIcon />}
                    onClick={handleAddPart}
                    sx={{ mt: 2 }}
                  >
                    Add New Part
                  </Button>
                </TableCell>
              </TableRow>
            ) : (
              filteredParts.map((part) => (
                <TableRow 
                  key={part.id}
                  hover
                  sx={{ 
                    '&:hover': { backgroundColor: '#f9f9f9' },
                    backgroundColor: part.is_low_stock ? '#fff3e0' : 'inherit'
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {part.part_number}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2">{part.part_name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {part.manufacturer || 'No manufacturer'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getCategoryIcon(part.category)}
                      <Chip 
                        label={part.category} 
                        size="small"
                        sx={{ 
                          backgroundColor: `${getCategoryColor(part.category)}15`,
                          color: getCategoryColor(part.category)
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(part.purchase_price)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%',
                        backgroundColor: safeParseInt(part.quantity) === 0 ? '#e74c3c' : 
                                       part.is_low_stock ? '#f39c12' : '#2ecc71'
                      }} />
                      <Typography>{safeParseInt(part.quantity)} pcs</Typography>
                      {part.is_low_stock && (
                        <Chip 
                          label="Low Stock" 
                          size="small" 
                          color="warning"
                          sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {formatCurrency(calculateInventoryValue(part))}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOnIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {part.location || 'Main Store'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      <Tooltip title="View Details">
                        <IconButton 
                          size="small" 
                          onClick={() => handleView(part)}
                          sx={{ color: '#3498db' }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Part">
                        <IconButton 
                          size="small"
                          sx={{ color: '#f39c12' }}
                          onClick={() => handleEdit(part)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Stock Adjustment">
                        <IconButton 
                          size="small"
                          sx={{ color: '#9b59b6' }}
                          onClick={() => handleAdjustment(part)}
                        >
                          <BuildCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Part">
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeleteClick(part)}
                          sx={{ color: '#e74c3c' }}
                          disabled={safeParseInt(part.quantity) > 0}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Enhanced Add Part Dialog with Working Search */}
      <Dialog open={openAddPartDialog} onClose={() => setOpenAddPartDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              {addPartMode === 'existing' ? '📦 Add to Existing Part' : '➕ Add New Computer Part'}
            </Typography>
            <IconButton onClick={() => setOpenAddPartDialog(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {/* Mode Selection */}
          <Card sx={{ mb: 3, backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
            <CardContent>
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Select Operation
                </FormLabel>
                <RadioGroup
                  row
                  value={addPartMode}
                  onChange={(e) => {
                    setAddPartMode(e.target.value);
                    setSelectedExistingPart(null);
                    setShowSearchResults(false);
                    if (e.target.value === 'new') {
                      generateNewPartNumber();
                    }
                  }}
                >
                  <FormControlLabel 
                    value="new" 
                    control={<Radio />} 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PostAddIcon color="primary" />
                        <Typography>Create New Part</Typography>
                      </Box>
                    } 
                  />
                  <FormControlLabel 
                    value="existing" 
                    control={<Radio />} 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AddShoppingCartIcon color="secondary" />
                        <Typography>Add to Existing Part (Increase Stock)</Typography>
                      </Box>
                    } 
                  />
                </RadioGroup>
                <FormHelperText>
                  {addPartMode === 'new' 
                    ? 'Create a brand new computer part in the inventory' 
                    : 'Search for an existing part and add more quantity to its stock'}
                </FormHelperText>
              </FormControl>
            </CardContent>
          </Card>

          {addPartMode === 'existing' ? (
            // Existing Part Search Section
            <>
              {!selectedExistingPart ? (
                <>
                  <Grid container spacing={2} alignItems="flex-end" sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={8}>
                      <TextField
                        fullWidth
                        label="Search by Name, Part Number, or Category"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearchExisting()}
                        placeholder="e.g., Intel i5, RAM, SSD, PART24020001"
                        variant="outlined"
                        size="medium"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Button
                        variant="contained"
                        onClick={handleSearchExisting}
                        disabled={searching || !searchQuery.trim()}
                        startIcon={searching ? <CircularProgress size={20} /> : <SearchIcon />}
                        fullWidth
                        size="large"
                        sx={{ height: '56px' }}
                      >
                        {searching ? 'Searching...' : 'Search Parts'}
                      </Button>
                    </Grid>
                  </Grid>

                  {/* Search Results */}
                  {showSearchResults && searchResults.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                        Search Results ({searchResults.length} parts found)
                      </Typography>
                      
                      <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Part Name</TableCell>
                              <TableCell>Part Number</TableCell>
                              <TableCell>Category</TableCell>
                              <TableCell align="right">Price</TableCell>
                              <TableCell align="right">Stock</TableCell>
                              <TableCell>Location</TableCell>
                              <TableCell align="center">Action</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {searchResults
                              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                              .map((part) => (
                                <TableRow 
                                  key={part.id}
                                  hover
                                  sx={{ 
                                    backgroundColor: part.is_low_stock ? '#fff3e0' : 'inherit',
                                    cursor: 'pointer'
                                  }}
                                >
                                  <TableCell>
                                    <Typography variant="body2" fontWeight="bold">
                                      {part.part_name}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      size="small" 
                                      label={part.part_number} 
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell>{part.category}</TableCell>
                                  <TableCell align="right">{formatCurrency(part.purchase_price)}</TableCell>
                                  <TableCell align="right">
                                    <Chip 
                                      size="small" 
                                      label={part.quantity}
                                      color={getStockStatusColor(part.quantity, part.min_stock_level)}
                                      sx={{ minWidth: 60 }}
                                    />
                                  </TableCell>
                                  <TableCell>{part.location || 'Main Store'}</TableCell>
                                  <TableCell align="center">
                                    <Button
                                      variant="contained"
                                      size="small"
                                      color="primary"
                                      onClick={() => handleSelectExistingPart(part)}
                                      startIcon={<CheckIcon />}
                                    >
                                      Select
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={searchResults.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                      />
                    </Box>
                  )}

                  {/* Show message if no results */}
                  {showSearchResults && searchResults.length === 0 && !searching && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      No parts found. Try a different search term or create a new part.
                    </Alert>
                  )}
                </>
              ) : (
                <Box>
                  <Alert 
                    severity="success" 
                    sx={{ mb: 2 }}
                    action={
                      <Button color="inherit" size="small" onClick={handleClearSelected}>
                        Change
                      </Button>
                    }
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      Selected Part: {selectedExistingPart.part_name}
                    </Typography>
                    <Typography variant="body2">
                      Part Number: {selectedExistingPart.part_number} | 
                      Current Stock: <strong>{selectedExistingPart.quantity}</strong> | 
                      Price: {formatCurrency(selectedExistingPart.purchase_price)}
                      {selectedExistingPart.quantity_source && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          (Quantity source: {selectedExistingPart.quantity_source})
                        </Typography>
                      )}
                    </Typography>
                  </Alert>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Quantity to Add *"
                        type="number"
                        value={additionalQuantity}
                        onChange={(e) => setAdditionalQuantity(e.target.value)}
                        InputProps={{ inputProps: { min: 1, step: 1 } }}
                        helperText="Enter the number of units to add to current stock"
                        required
                        variant="outlined"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Card variant="outlined" sx={{ p: 2, backgroundColor: '#f5f5f5', height: '100%' }}>
                        <Typography variant="body2" color="text.secondary">Stock Summary</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Current:</Typography>
                            <Typography variant="h6">{selectedExistingPart.quantity}</Typography>
                          </Box>
                          <Typography variant="h5" color="primary">+</Typography>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Adding:</Typography>
                            <Typography variant="h6">{safeParseInt(additionalQuantity) || 0}</Typography>
                          </Box>
                          <Typography variant="h5" color="primary">=</Typography>
                          <Box>
                            <Typography variant="caption" color="text.secondary">New Total:</Typography>
                            <Typography variant="h6" color="success.main" fontWeight="bold">
                              {safeParseInt(selectedExistingPart.quantity) + safeParseInt(additionalQuantity)}
                            </Typography>
                          </Box>
                        </Box>
                      </Card>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Location</InputLabel>
                        <Select
                          name="location"
                          value={newPartData.location}
                          onChange={(e) => setNewPartData({...newPartData, location: e.target.value})}
                          label="Location"
                        >
                          {locations.map(loc => (
                            <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                          ))}
                          <MenuItem value="Other">Other (specify in notes)</MenuItem>
                        </Select>
                        <FormHelperText>
                          Current location: {selectedExistingPart.location || 'Main Store'}
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </>
          ) : (
            // New Part Form
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Part Name *"
                  name="part_name"
                  value={newPartData.part_name}
                  onChange={handleNewPartChange}
                  required
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Part Number"
                  name="part_number"
                  value={newPartData.part_number}
                  onChange={handleNewPartChange}
                  margin="normal"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={generateNewPartNumber}>
                          <Tooltip title="Generate New Number">
                            <RefreshIcon fontSize="small" />
                          </Tooltip>
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Category *</InputLabel>
                  <Select
                    name="category"
                    value={newPartData.category}
                    onChange={handleNewPartChange}
                    label="Category *"
                  >
                    {partCategories.map(cat => (
                      <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Manufacturer</InputLabel>
                  <Select
                    name="manufacturer"
                    value={newPartData.manufacturer}
                    onChange={handleNewPartChange}
                    label="Manufacturer"
                  >
                    {manufacturers.map(mfg => (
                      <MenuItem key={mfg} value={mfg}>{mfg}</MenuItem>
                    ))}
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Purchase Price (LKR) *"
                  name="purchase_price"
                  value={newPartData.purchase_price}
                  onChange={handleNewPartChange}
                  type="number"
                  InputProps={{ 
                    inputProps: { min: 0, step: 0.01 },
                    startAdornment: <InputAdornment position="start">Rs.</InputAdornment>
                  }}
                  required
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Selling Price (LKR)"
                  name="selling_price"
                  value={newPartData.selling_price}
                  onChange={handleNewPartChange}
                  type="number"
                  InputProps={{ 
                    inputProps: { min: 0, step: 0.01 },
                    startAdornment: <InputAdornment position="start">Rs.</InputAdornment>
                  }}
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Initial Quantity"
                  name="quantity"
                  value={newPartData.quantity}
                  onChange={handleNewPartChange}
                  type="number"
                  InputProps={{ inputProps: { min: 0 } }}
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Min Stock Level"
                  name="min_stock_level"
                  value={newPartData.min_stock_level}
                  onChange={handleNewPartChange}
                  type="number"
                  InputProps={{ inputProps: { min: 0 } }}
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Location</InputLabel>
                  <Select
                    name="location"
                    value={newPartData.location}
                    onChange={handleNewPartChange}
                    label="Location"
                  >
                    {locations.map(loc => (
                      <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                    ))}
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Supplier"
                  name="supplier"
                  value={newPartData.supplier}
                  onChange={handleNewPartChange}
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Specifications"
                  name="specifications"
                  value={newPartData.specifications}
                  onChange={handleNewPartChange}
                  multiline
                  rows={2}
                  margin="normal"
                  placeholder="e.g., 3.4GHz, 8GB, 512GB, etc."
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  name="notes"
                  value={newPartData.notes}
                  onChange={handleNewPartChange}
                  multiline
                  rows={2}
                  margin="normal"
                />
              </Grid>

              {/* Calculation Display */}
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ mt: 2, backgroundColor: '#f8f9fa' }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AttachMoneyIcon fontSize="small" /> Calculations
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Total Value:
                        </Typography>
                        <Typography variant="body1" color="primary" fontWeight="bold">
                          {formatCurrency(
                            safeParseInt(newPartData.quantity) * safeParseFloat(newPartData.purchase_price)
                          )}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Unit Price:
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {formatCurrency(newPartData.purchase_price || 0)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            variant="outlined" 
            onClick={() => setOpenAddPartDialog(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleAddPartSubmit}
            disabled={
              saving || 
              (addPartMode === 'existing' && (!selectedExistingPart || !additionalQuantity))
            }
            sx={{
              backgroundColor: addPartMode === 'existing' ? '#27ae60' : '#3498db',
              '&:hover': { 
                backgroundColor: addPartMode === 'existing' ? '#219653' : '#2980b9' 
              },
              minWidth: 150
            }}
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          >
            {saving ? 'Saving...' : (addPartMode === 'existing' ? 'Add to Stock' : 'Create Part')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="md" fullWidth>
        {selectedPart && (
          <>
            <DialogTitle>
              <Typography variant="h6">
                Part Details: {selectedPart.part_name}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Part Number:</Typography>
                  <Typography variant="body1">{selectedPart.part_number}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Category:</Typography>
                  <Typography variant="body1">{selectedPart.category}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Manufacturer:</Typography>
                  <Typography variant="body1">{selectedPart.manufacturer || 'Not specified'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Supplier:</Typography>
                  <Typography variant="body1">{selectedPart.supplier || 'Not specified'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Purchase Price:</Typography>
                  <Typography variant="body1">{formatCurrency(selectedPart.purchase_price)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Selling Price:</Typography>
                  <Typography variant="body1">{formatCurrency(selectedPart.selling_price)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Quantity:</Typography>
                  <Typography variant="body1">{safeParseInt(selectedPart.quantity)} pcs</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Min Stock Level:</Typography>
                  <Typography variant="body1">{selectedPart.min_stock_level}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Location:</Typography>
                  <Typography variant="body1">{selectedPart.location || 'Main Store'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Inventory Value:</Typography>
                  <Typography variant="body1">{formatCurrency(calculateInventoryValue(selectedPart))}</Typography>
                </Grid>
                {selectedPart.specifications && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Specifications:</Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                      {selectedPart.specifications}
                    </Typography>
                  </Grid>
                )}
                {selectedPart.notes && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Notes:</Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                      {selectedPart.notes}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
              <Button 
                onClick={() => {
                  setOpenViewDialog(false);
                  handleEdit(selectedPart);
                }}
                variant="contained"
                sx={{ backgroundColor: '#f39c12' }}
              >
                Edit Part
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={openFormDialog} onClose={() => setOpenFormDialog(false)} maxWidth="md" fullWidth>
        <PartForm />
      </Dialog>

      <AdjustmentDialog />

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete Part</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{selectedPart?.part_name}"? 
            {safeParseInt(selectedPart?.quantity) > 0 && (
              <Typography color="error" sx={{ mt: 1 }}>
                Warning: This part has {safeParseInt(selectedPart?.quantity)} units in stock. 
                You cannot delete parts with existing stock.
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button 
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={safeParseInt(selectedPart?.quantity) > 0}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          backgroundColor: '#3498db',
          '&:hover': { backgroundColor: '#2980b9' }
        }}
        onClick={handleAddPart}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default InventoryManagement;